'use client';

import type { DialogTriggerProps } from '@radix-ui/react-dialog';

import { createContext, forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { type ContentProps, Drawer as Primitive } from 'vaul';

import CrossIcon from '@publicImages/svg/close-without-bg.svg?static';

import type { TKeyEventGroup } from '@/ducks/a11y/types';

import useFocusTrap from '@/ducks/a11y/hooks/useFocusTrap';
import { useMergeRefs } from '@/helpers/mergeRefs';
import { useIsMobile } from '@/hooks/useIsDevice';

import styles from './Drawer.module.scss';

type TContext = {
  isOpen?: boolean;
};
const Context = createContext<TContext>({});

export const Root = ({
  onOpenChange: onOpenChangeProp,
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof Primitive.Root>) => {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(props.defaultOpen);

  const onOpenChange = useCallback(
    (newState: boolean) => {
      onOpenChangeProp?.(newState);
      if (props.open === undefined) {
        setTimeout(() => setIsOpen(newState));
      }
    },
    [onOpenChangeProp, props.open],
  );

  useEffect(() => {
    if (props.open !== undefined) {
      setTimeout(() => setIsOpen(props.open));
    }
  }, [props.open]);

  return (
    <Context.Provider value={{ isOpen }}>
      <Primitive.Root
        handleOnly={isMobile}
        onOpenChange={onOpenChange}
        shouldScaleBackground={shouldScaleBackground}
        {...props}
      />
    </Context.Provider>
  );
};
Root.displayName = 'Drawer';

const clickOnTouch = (e: React.TouchEvent<HTMLButtonElement>) => e.currentTarget.click();

export const Content = forwardRef<
  HTMLDivElement,
  ContentProps & {
    closeSlot?: React.ReactNode;
    innerContentClassName?: string;
    overlayClassName?: string;
  }
>(({ children, className, closeSlot, innerContentClassName, overlayClassName, title, ...props }, ref) => {
  const { isOpen } = useContext(Context);

  const contentRef = useRef<HTMLDivElement>(null);
  const mergedRefs = useMergeRefs(ref, contentRef);

  const disableEvents = ['tab'] as TKeyEventGroup[];
  useFocusTrap({ disableEvents, element: contentRef.current, isOpened: isOpen });

  return (
    <Primitive.Portal>
      <Primitive.Overlay className={cn(styles.overlay, overlayClassName)} />

      <Primitive.Content className={cn(styles.content, className)} ref={mergedRefs} {...props}>
        {closeSlot !== undefined ? (
          closeSlot
        ) : (
          <Primitive.Close
            aria-label={`Close ${title} dialog`}
            autoFocus
            className={styles.close}
            // Making button more responsive on iPhone
            onTouchStart={clickOnTouch}
          >
            <CrossIcon />
          </Primitive.Close>
        )}

        <div className={cn(styles.scrollable, innerContentClassName)}>{children}</div>
      </Primitive.Content>
    </Primitive.Portal>
  );
});
Content.displayName = 'DrawerContent';

export const Trigger = forwardRef<HTMLButtonElement, React.ComponentProps<'button'> & DialogTriggerProps>(
  (props, ref) => <Primitive.Trigger aria-expanded={undefined} ref={ref} {...props} />,
);
Trigger.displayName = 'DrawerTrigger';

export const Title = Primitive.Title;
export const Description = Primitive.Description;
export const Close = Primitive.Close;
